import { useState, useMemo } from 'react';
import { Loader2, Cable, Search, Check, User, ArrowRight } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  useActiveUsers,
  useRecordCableUsage,
  useTransferOwnership,
} from '../hooks/useCable';
import { useWeeklyVisits } from '@/features/visits/hooks/useWeeklyVisits';
import type { CableSpool } from '@/api/legacy';
import type { OwnershipFilterValue } from '@/api/visits';

interface CableUsageSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  spool: CableSpool | null;
}

type FilterValue = OwnershipFilterValue[] | 'all';

const OWNERSHIP_FILTER_KEY = 'visits-ownership-filter';

function loadOwnershipFilter(): FilterValue {
  try {
    const saved = localStorage.getItem(OWNERSHIP_FILTER_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed === 'all' || (Array.isArray(parsed) && parsed.length > 0)) {
        return parsed;
      }
    }
  } catch {
    // Ignore
  }
  return ['own'];
}

export function CableUsageSheet({
  open,
  onOpenChange,
  spool,
}: CableUsageSheetProps) {
  const [amount, setAmount] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [transferTo, setTransferTo] = useState('');

  // Use the same filter as visits page
  const ownershipFilter = useMemo(() => loadOwnershipFilter(), []);
  const { data: weeklyData, isLoading: clientsLoading } = useWeeklyVisits(ownershipFilter);
  const { data: activeUsers } = useActiveUsers();

  const recordUsage = useRecordCableUsage();
  const transferOwnership = useTransferOwnership();

  // Filter clients by search
  const filteredClients = useMemo(() => {
    if (!weeklyData?.visits) return [];
    const q = search.toLowerCase().trim();
    if (!q) return weeklyData.visits;
    return weeklyData.visits.filter((v) =>
      v.client.name.toLowerCase().includes(q)
    );
  }, [weeklyData?.visits, search]);

  const selectedClient = weeklyData?.visits.find(
    (v) => v.client.id === selectedClientId
  );
  const userOptions = useMemo(() => {
    if (!activeUsers) return [];
    return Object.entries(activeUsers)
      .filter(([username]) => !username.endsWith('-tel'))
      .map(([username, displayName]) => ({
        username,
        displayName,
      }));
  }, [activeUsers]);

  const remaining = spool ? parseFloat(spool.ostatok) : 0;
  const amountNum = parseFloat(amount) || 0;
  const isPending = recordUsage.isPending || transferOwnership.isPending;

  const isValid =
    spool &&
    selectedClientId &&
    amountNum > 0 &&
    amountNum <= remaining;

  const handleSubmit = () => {
    if (!isValid || !spool || !selectedClient) return;

    recordUsage.mutate(
      {
        buhta_id: parseInt(spool.id),
        amount: amountNum,
        company: selectedClient.client.name,
      },
      {
        onSuccess: () => {
          setAmount('');
          setSelectedClientId(null);
          setSearch('');
          onOpenChange(false);
        },
      }
    );
  };

  const handleClaimSpool = () => {
    if (!spool) return;
    transferOwnership.mutate({ buhtaId: parseInt(spool.id), username: '' });
  };

  const handleTransferSpool = () => {
    if (!spool || !transferTo) return;
    transferOwnership.mutate(
      { buhtaId: parseInt(spool.id), username: transferTo },
      {
        onSuccess: () => {
          setTransferTo('');
        },
      },
    );
  };

  // Reset state when sheet closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setAmount('');
      setSelectedClientId(null);
      setSearch('');
      setTransferTo('');
    }
    onOpenChange(newOpen);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Cable className="h-5 w-5" />
            Бухта #{spool?.name}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto mt-4 space-y-4">
          {/* Spool info */}
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-medium">Бухта #{spool?.name}</span>
              <span className="text-sm text-muted-foreground">
                Остаток: {remaining} м
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between gap-3 text-sm">
              {spool?.isMyOwn ? (
                <span className="rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                  Моя
                </span>
              ) : (
                <span className="flex min-w-0 items-center gap-1 text-muted-foreground">
                  <User className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{spool?.username || 'Без владельца'}</span>
                </span>
              )}
            </div>
          </div>

          {/* Ownership controls */}
          {spool && (
            <div className="space-y-2">
              <Label>Владелец</Label>
              {spool.isMyOwn ? (
                <div className="flex items-center gap-2">
                  <Select value={transferTo} onValueChange={setTransferTo}>
                    <SelectTrigger className="min-w-0 flex-1">
                      <SelectValue placeholder="Передать сотруднику..." />
                    </SelectTrigger>
                    <SelectContent>
                      {userOptions.map((user) => (
                        <SelectItem key={user.username} value={user.username}>
                          {user.displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleTransferSpool}
                    disabled={!transferTo || isPending}
                    aria-label="Передать бухту"
                  >
                    {transferOwnership.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowRight className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleClaimSpool}
                  disabled={isPending}
                >
                  {transferOwnership.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Забрать бухту себе
                </Button>
              )}
            </div>
          )}

          {/* Client search */}
          <div className="space-y-2">
            <Label>Клиент</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Поиск клиента..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Client list */}
          <div className="border rounded-md max-h-48 overflow-y-auto">
            {clientsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {search ? 'Клиенты не найдены' : 'Нет клиентов'}
              </div>
            ) : (
              filteredClients.map((visit) => (
                <button
                  key={visit.client.id}
                  type="button"
                  onClick={() => setSelectedClientId(visit.client.id)}
                  className={cn(
                    'w-full flex items-center gap-3 p-3 text-left border-b last:border-b-0 transition-colors',
                    selectedClientId === visit.client.id && 'bg-accent'
                  )}
                >
                  <div
                    className={cn(
                      'h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                      selectedClientId === visit.client.id
                        ? 'border-primary bg-primary'
                        : 'border-muted-foreground'
                    )}
                  >
                    {selectedClientId === visit.client.id && (
                      <Check className="h-2.5 w-2.5 text-primary-foreground" />
                    )}
                  </div>
                  <span className="truncate">{visit.client.name}</span>
                </button>
              ))
            )}
          </div>

          {/* Amount input */}
          <div className="space-y-2">
            <Label htmlFor="cable-amount">Расход (метров)</Label>
            <Input
              id="cable-amount"
              type="number"
              placeholder="Введите метраж..."
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min={1}
              max={remaining}
            />
            {amountNum > 0 && (
              <div className="text-xs text-muted-foreground">
                Остаток после: {remaining - amountNum} м
              </div>
            )}
            {amountNum > remaining && (
              <p className="text-xs text-destructive">
                Нельзя списать больше остатка ({remaining} м)
              </p>
            )}
          </div>
        </div>

        {/* Submit button */}
        <div className="pt-4 border-t mt-4">
          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={!isValid || isPending}
          >
            {recordUsage.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Cable className="h-4 w-4 mr-2" />
            )}
            Списать кабель
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
